import { Component, ElementRef, OnDestroy, effect, input, viewChild } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import videojs from 'video.js';
import videojsQualitySelector from '@silvermine/videojs-quality-selector';
import { WasabiImgPipe } from 'ag-common-svc/shared/pipes/wasabi-img.pipe';
import { Headshot, HeadshotKeys } from '@ag-common-lib/lib/models/utils/headshot.model';
import ShareButton from './custom-plugins/share-button.component';
import { VimeoShareActionKey } from './components/vimeo-video-share-popup/vimeo-video-share-popup.component';
import { isEmpty } from 'lodash';

videojsQualitySelector(videojs);

@UntilDestroy()
@Component({
  selector: 'ag-shr-vimeo-video',
  templateUrl: './vimeo-video.component.html',
  styleUrls: ['./vimeo-video.component.scss'],
  host: {
    class: 'vimeo-video',
    '[class.vimeo-video--started]': 'hasStartedPlayback',
    '[class.vimeo-video--hovered]': 'isPointerOverVideo',
    '[class.vimeo-video--compact]': 'isCompactLayout',
    '[class.vimeo-video--vertical]': 'isVerticalLayout',
    '[class.vimeo-video--player-ready]': 'isPlayerReady',
    '[class.vimeo-video--fullscreen]': 'isFullscreen',
    '[class.vimeo-video--fullscreen-controls-idle]': 'isFullscreenControlsIdle',
  },
})
export class VimeoVideoComponent implements OnDestroy {
  protected readonly privateVideoMessage =
    'This video is private and cannot be shared or viewed outside of the Alliance Agent Portal.';

  protected readonly videoElementSig = viewChild<ElementRef<HTMLVideoElement>>('vimeoVideoRef');
  protected readonly playerShellElementSig = viewChild<ElementRef<HTMLDivElement>>('playerShellRef');
  protected readonly overlayButtonElementSig = viewChild<ElementRef<HTMLButtonElement>>('overlayButtonRef');
  protected readonly controlsToggleElementSig = viewChild<ElementRef<HTMLButtonElement>>('controlsToggleRef');

  readonly shareVideoSig = input(false, { alias: 'shareVideo' });
  readonly autoplaySig = input(false, { alias: 'autoplay' });
  readonly mutedSig = input(false, { alias: 'muted' });
  readonly showControlsSig = input(true, { alias: 'showControls' });
  readonly preloadModeSig = input<'none' | 'auto' | 'metadata'>('none', { alias: 'preloadMode' });
  readonly headshotSig = input<Headshot | undefined>(undefined, { alias: 'headshot' });
  readonly vimeoVideoSig = input<any>(undefined, { alias: 'vimeoVideo' });
  readonly layoutAspectRatioSig = input<string | undefined>(undefined, { alias: 'layoutAspectRatio' });

  protected sources: any[] = [];
  protected poster: string;
  protected isPointerOverVideo = false;
  protected hasStartedPlayback = false;
  protected isPlaying = false;
  protected isFullscreen = false;
  protected isFullscreenControlsIdle = false;
  protected isPlayerReady = false;
  protected isVerticalVideo = false;
  protected isVerticalLayout = false;
  protected isCompactAspectRatioLayout = false;
  protected isNarrowVideo = false;
  protected isCompactLayout = false;
  protected areControlsExpanded = false;
  protected isSharePanelVisible = false;
  protected shouldShowOverlayButton = true;
  protected shouldCenterOverlayButton = false;
  protected shouldShowControlsToggle = false;
  protected overlayButtonIcon: 'play' | 'pause' = 'play';
  protected shareLink = '';
  protected hasShareLink = false;
  protected canOpenSharePanel = false;
  protected isPrivateVideo = false;
  protected copyButtonText = 'Copy';
  protected popupContainerElement: HTMLElement | null = null;

  private _player: any;
  private _videoLink: string;
  private _videoTitle: string;
  private _resizeObserver: ResizeObserver;
  private _wasControlsManuallyToggled = false;
  private _isResponseVerticalVideo = false;
  private _wasCompactLayoutBeforeFullscreen = false;
  private _fullscreenControlsIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private _fullscreenActivityCleanupFns: Array<() => void> = [];
  private _copyButtonResetTimer: ReturnType<typeof setTimeout> | null = null;
  private _initializedVideoElement: HTMLVideoElement | null = null;
  private readonly fullscreenControlsIdleDelayMs = 3000;

  constructor(private wasabiImgPipe: WasabiImgPipe) {
    effect(() => {
      this.applyVideoInputs(this.headshotSig(), this.vimeoVideoSig());
    });

    effect(() => {
      this.syncLayoutAspectRatioState(this.layoutAspectRatioSig());
      this.syncCompactLayout();
    });

    effect(() => {
      const showControls = this.showControlsSig();

      if (this._player) {
        this._player.controls(showControls);
      }

      this.syncCompactLayout();
      this.scheduleFullscreenControlsIdle();
    });

    effect(() => {
      const videoElement = this.videoElementSig()?.nativeElement;
      const playerShellElement = this.playerShellElementSig()?.nativeElement;

      if (!videoElement || !playerShellElement) {
        return;
      }

      if (this._initializedVideoElement === videoElement) {
        return;
      }

      this.areControlsExpanded = false;
      this.observePlayerSize(playerShellElement);
      this.initializePlayer(videoElement);
      this._initializedVideoElement = videoElement;
    });

    effect(() => {
      this.moveCustomControlsIntoPlayer();
    });
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this.clearFullscreenControlsIdleTimer();
    this.clearFullscreenActivityListeners();
    this.clearCopyButtonResetTimer();

    if (this._player) {
      this._player.dispose();
    }

    this._initializedVideoElement = null;
  }

  protected onVideoMouseEnter(): void {
    this.isPointerOverVideo = true;
    this.syncViewState();
  }

  protected onVideoMouseLeave(): void {
    this.isPointerOverVideo = false;
    this.syncViewState();
  }

  protected handleOverlayButtonClick(): void {
    if (!this._player) {
      return;
    }

    if (this.isPlaying) {
      this._player.pause();
      return;
    }

    void this._player.play();
  }

  protected toggleControlsVisibility(): void {
    this._wasControlsManuallyToggled = true;
    this.areControlsExpanded = !this.areControlsExpanded;
    this.syncControlBarVisibility();
    this.syncViewState();
  }

  protected toggleSharePanelVisibility(): void {
    if (!this.canOpenSharePanel) {
      return;
    }

    this.isSharePanelVisible = !this.isSharePanelVisible;

    if (!this.isSharePanelVisible) {
      this.resetCopyButtonText();
    }

    this.isFullscreenControlsIdle = false;
    this.scheduleFullscreenControlsIdle();
  }

  protected closeSharePanel(): void {
    this.isSharePanelVisible = false;
    this.resetCopyButtonText();
    this.isFullscreenControlsIdle = false;
    this.scheduleFullscreenControlsIdle();
  }

  protected readonly handleCopyButtonClick = (): void => {
    this.copyShareLink();
  };

  protected shareTo(actionKey: VimeoShareActionKey): void {
    const shareUrl = this.buildShareUrl(actionKey);

    if (!shareUrl) {
      return;
    }

    if (actionKey === 'mail') {
      location.href = shareUrl;
      return;
    }

    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  }

  private initializePlayer(videoElement: HTMLVideoElement): void {
    this.isPlayerReady = false;

    const options = {
      controls: this.showControlsSig(),
      autoplay: this.autoplaySig(),
      preload: this.preloadModeSig(),
      muted: this.mutedSig(),
      disablePictureInPicture: false,
      html5: {
        vhs: {
          overrideNative: true,
        },
      },
    };

    if (this._player) {
      this.clearFullscreenControlsIdleTimer();
      this.clearFullscreenActivityListeners();
      this._player.dispose();
    }

    this._player = videojs(videoElement, options, this.onPlayerReady.bind(this));
  }

  private onPlayerReady(): void {
    this.popupContainerElement = this._player?.el?.() ?? null;
    this.moveCustomControlsIntoPlayer();
    this.removeDefaultPlayControls();

    this._player.controlBar.addChild('QualitySelector');

    this.ensurePictureInPictureControl();

    this.bindFullscreenActivityEvents();
    this.bindPlayerStateEvents();
    this.syncCompactLayout();
    this.syncControlBarVisibility();

    if (this.shareVideoSig()) {
      videojs.registerComponent('ShareButton', ShareButton);

      this._player.controlBar.addChild('ShareButton', {
        videoLink: this._videoLink,
        videoTitle: this._videoTitle,
        isPrivate: this.isPrivateVideo,
      });

      this.moveShareButtonToPlayerCorner();

      this._player.on('share', () => {
        this.toggleSharePanelVisibility();
      });
    }

    this.isPlayerReady = true;
  }

  private removeDefaultPlayControls(): void {
    if (this._player?.controlBar?.playToggle) {
      this._player.controlBar.removeChild(this._player.controlBar.playToggle);
    }

    if (this._player?.bigPlayButton) {
      this._player.bigPlayButton.hide();
    }
  }

  private bindPlayerStateEvents(): void {
    this._player.on('loadedmetadata', () => {
      this.isVerticalVideo = this._isResponseVerticalVideo || this._player.videoHeight() > this._player.videoWidth();
      this.syncLayoutAspectRatioState(this.layoutAspectRatioSig());
      this.ensurePictureInPictureControl();
      this.syncCompactLayout();
      this.syncViewState();
    });

    this._player.on('play', () => {
      this.hasStartedPlayback = true;
      this.isPlaying = true;
      this.syncCompactLayout();
      this.syncViewState();
    });

    this._player.on('pause', () => {
      this.hasStartedPlayback = true;
      this.isPlaying = false;
      this.syncCompactLayout();
      this.syncViewState();
    });

    this._player.on('ended', () => {
      this.isPlaying = false;
      this.syncCompactLayout();
      this.syncViewState();
    });

    this._player.on('fullscreenchange', () => {
      const isFullscreen = !!this._player?.isFullscreen?.();

      if (isFullscreen && !this.isFullscreen) {
        this._wasCompactLayoutBeforeFullscreen = this.isCompactLayout || this.isVerticalLayout || this.isNarrowVideo;
      } else if (!isFullscreen) {
        this._wasCompactLayoutBeforeFullscreen = false;
      }

      this.isFullscreen = isFullscreen;
      this.isFullscreenControlsIdle = false;
      this._wasControlsManuallyToggled = false;
      this.syncCompactLayout();
      this.syncViewState();
      this.scheduleFullscreenControlsIdle();
    });
  }

  private applyVideoInputs(headshot: Headshot | undefined, response: any): void {
    this.sources = Array.isArray(response?.files)
      ? response.files
          .filter(source => source?.rendition !== 'adaptive')
          .sort((a, b) =>
            `${a.public_name}`.localeCompare(b.public_name, 'en', {
              numeric: true,
              sensitivity: 'base',
              ignorePunctuation: true,
            }),
          )
      : [];

    this.poster =
      !isEmpty(headshot) && !!headshot?.[HeadshotKeys.wasabiPath]
        ? this.wasabiImgPipe.transform(headshot[HeadshotKeys.wasabiPath], {
            cropperPayload: headshot[HeadshotKeys.imageCropperPayload],
          })
        : response?.pictures?.base_link;

    this.isPrivateVideo = this.isVideoPrivate(response);
    this._videoLink = this.isPrivateVideo ? null : this.resolveShareLink(response);
    this._videoTitle = response?.name || null;
    this._isResponseVerticalVideo = this.hasVerticalVideoDimensions(response);
    this.isVerticalVideo = this._isResponseVerticalVideo;
    this.syncLayoutAspectRatioState(this.layoutAspectRatioSig());
    this.isCompactLayout =
      !this.isFullscreen && (this.isVerticalLayout || this.isCompactAspectRatioLayout || this.isNarrowVideo);
    this.areControlsExpanded = false;
    this.shareLink = this._videoLink ?? '';
    this.hasShareLink = !!this.shareLink;
    this.canOpenSharePanel = this.isPrivateVideo || this.hasShareLink;
    this.syncShareButtonState();
    this.syncViewState();
  }

  private hasVerticalVideoDimensions(response: any): boolean {
    const width = Number(response?.width);
    const height = Number(response?.height);

    return Number.isFinite(width) && Number.isFinite(height) && height > width;
  }

  private syncLayoutAspectRatioState(aspectRatio: string | undefined): void {
    const parsedAspectRatio = this.parseAspectRatio(aspectRatio);

    if (!parsedAspectRatio) {
      this.isVerticalLayout = this.isVerticalVideo;
      this.isCompactAspectRatioLayout = false;
      return;
    }

    this.isVerticalLayout = parsedAspectRatio.height > parsedAspectRatio.width;
    this.isCompactAspectRatioLayout = parsedAspectRatio.height >= parsedAspectRatio.width;
  }

  private parseAspectRatio(aspectRatio: string | undefined): { width: number; height: number } | null {
    if (!aspectRatio) {
      return null;
    }

    const [width, height] = `${aspectRatio}`.split('/').map(value => Number(value.trim()));

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return null;
    }

    return { width, height };
  }

  private isVideoPrivate(response: any): boolean {
    const privacyView = response?.privacy?.view;

    return typeof privacyView === 'string' && privacyView !== 'anybody';
  }

  private resolveShareLink(response: any): string | null {
    if (response?.link) {
      return response.link;
    }

    const videoId = this.resolveVideoId(response);

    if (videoId) {
      return `https://vimeo.com/${videoId}`;
    }

    return response?.files?.[0]?.link ?? null;
  }

  private resolveVideoId(response: any): string | null {
    const responseUri = response?.uri;

    if (typeof responseUri === 'string') {
      const uriVideoId = responseUri.split('/').pop();

      if (uriVideoId) {
        return uriVideoId;
      }
    }

    if (typeof response?.link === 'string') {
      const linkVideoId = response.link.split('/').pop();

      if (linkVideoId) {
        return linkVideoId;
      }
    }

    return null;
  }

  private ensurePictureInPictureControl(): void {
    const controlBar = this._player?.controlBar;

    if (!controlBar) {
      return;
    }

    let pictureInPictureControl = controlBar.getChild('PictureInPictureToggle');

    if (!pictureInPictureControl) {
      try {
        pictureInPictureControl = controlBar.addChild(
          'PictureInPictureToggle',
          {},
          this.getPictureInPictureControlIndex(),
        );
      } catch {
        return;
      }
    }

    this.syncPictureInPictureControlState(pictureInPictureControl);
  }

  private getPictureInPictureControlIndex(): number | undefined {
    const children = this._player?.controlBar?.children?.() || [];
    const fullscreenControlIndex = children.findIndex(child => child?.name?.() === 'FullscreenToggle');

    return fullscreenControlIndex >= 0 ? fullscreenControlIndex : undefined;
  }

  private syncPictureInPictureControlState(control: any): void {
    const controlElement = control?.el?.();

    if (!controlElement) {
      return;
    }

    control.enable?.();
    controlElement.classList.remove('vjs-hidden', 'vjs-disabled');
    controlElement.removeAttribute('disabled');
    controlElement.setAttribute('aria-disabled', 'false');
  }

  private syncControlBarVisibility(): void {
    const controlBarElement = this._player?.controlBar?.el();

    if (!controlBarElement) {
      return;
    }

    controlBarElement.classList.toggle('vjs-controls-collapsed', !this.areControlsExpanded);
  }

  private bindFullscreenActivityEvents(): void {
    const playerElement = this._player?.el?.();

    if (!playerElement) {
      return;
    }

    this.clearFullscreenActivityListeners();

    ['mousemove', 'pointermove', 'mousedown', 'touchstart', 'touchmove', 'touchend', 'click', 'keydown'].forEach(
      eventName => {
        const listener = () => this.handleFullscreenUserActivity();

        playerElement.addEventListener(eventName, listener, { passive: true });
        this._fullscreenActivityCleanupFns.push(() => playerElement.removeEventListener(eventName, listener));
      },
    );
  }

  private clearFullscreenActivityListeners(): void {
    this._fullscreenActivityCleanupFns.forEach(cleanup => cleanup());
    this._fullscreenActivityCleanupFns = [];
  }

  private handleFullscreenUserActivity(): void {
    if (!this.isFullscreen) {
      return;
    }

    this.isFullscreenControlsIdle = false;
    this.scheduleFullscreenControlsIdle();
  }

  private scheduleFullscreenControlsIdle(): void {
    this.clearFullscreenControlsIdleTimer();

    if (!this.isFullscreen || !this.showControlsSig() || this.isSharePanelVisible) {
      this.isFullscreenControlsIdle = false;
      return;
    }

    this._fullscreenControlsIdleTimer = setTimeout(() => {
      if (!this.isFullscreen || !this.showControlsSig() || this.isSharePanelVisible) {
        this.isFullscreenControlsIdle = false;
        return;
      }

      this.isFullscreenControlsIdle = true;
    }, this.fullscreenControlsIdleDelayMs);
  }

  private clearFullscreenControlsIdleTimer(): void {
    if (this._fullscreenControlsIdleTimer) {
      clearTimeout(this._fullscreenControlsIdleTimer);
      this._fullscreenControlsIdleTimer = null;
    }
  }

  private async copyShareLink(): Promise<void> {
    if (!this.hasShareLink) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.shareLink);
      } else {
        this.copyShareLinkFallback();
      }

      this.copyButtonText = 'Copied';
      this.scheduleCopyButtonReset();
    } catch (error) {
      console.error('Error copying share link', error);
    }
  }

  private copyShareLinkFallback(): void {
    const input = document.createElement('input');
    input.value = this.shareLink;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.focus();
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }

  private scheduleCopyButtonReset(): void {
    this.clearCopyButtonResetTimer();
    this._copyButtonResetTimer = setTimeout(() => {
      this.copyButtonText = 'Copy';
      this._copyButtonResetTimer = null;
    }, 2000);
  }

  private clearCopyButtonResetTimer(): void {
    if (this._copyButtonResetTimer) {
      clearTimeout(this._copyButtonResetTimer);
      this._copyButtonResetTimer = null;
    }
  }

  private resetCopyButtonText(): void {
    this.clearCopyButtonResetTimer();
    this.copyButtonText = 'Copy';
  }

  private moveShareButtonToPlayerCorner(): void {
    const shareControl = this._player?.controlBar?.getChild('ShareButton');
    const shareElement = shareControl?.el?.();
    const playerElement = this._player?.el?.();

    if (!shareElement || !playerElement) {
      return;
    }

    playerElement.appendChild(shareElement);
  }

  private moveCustomControlsIntoPlayer(): void {
    const playerElement = this._player?.el?.();

    if (!playerElement) {
      return;
    }

    const overlayButtonElement = this.overlayButtonElementSig()?.nativeElement;
    const controlsToggleElement = this.controlsToggleElementSig()?.nativeElement;

    this.appendElementToPlayer(playerElement, overlayButtonElement);
    this.appendElementToPlayer(playerElement, controlsToggleElement);
  }

  private appendElementToPlayer(playerElement: HTMLElement, element?: HTMLElement): void {
    if (!element || element.parentElement === playerElement) {
      return;
    }

    playerElement.appendChild(element);
  }

  private syncShareButtonState(): void {
    const shareControl = this._player?.controlBar?.getChild('ShareButton');

    if (!shareControl?.updateState) {
      return;
    }

    shareControl.updateState({
      videoLink: this._videoLink,
      videoTitle: this._videoTitle,
      isPrivate: this.isPrivateVideo,
    });
  }

  private observePlayerSize(playerShellElement: HTMLDivElement): void {
    if (!playerShellElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    this._resizeObserver?.disconnect();

    this._resizeObserver = new ResizeObserver(entries => {
      const [entry] = entries;

      if (!entry) {
        return;
      }

      this.isNarrowVideo = entry.contentRect.width < 560;
      this.syncCompactLayout();
      this.syncViewState();
    });

    this._resizeObserver.observe(playerShellElement);
  }

  private syncCompactLayout(): void {
    this.isCompactLayout =
      !this.isFullscreen && (this.isVerticalLayout || this.isCompactAspectRatioLayout || this.isNarrowVideo);

    if (!this.hasStartedPlayback) {
      this.areControlsExpanded = false;
      this.syncControlBarVisibility();
      this.syncViewState();
      return;
    }

    if (this.isCompactLayout && this._wasControlsManuallyToggled) {
      this.syncControlBarVisibility();
      this.syncViewState();
      return;
    }

    if (this.isCompactLayout) {
      this.areControlsExpanded = false;
      this.syncControlBarVisibility();
      this.syncViewState();
      return;
    }

    this.areControlsExpanded = this.showControlsSig();
    this._wasControlsManuallyToggled = false;
    this.syncControlBarVisibility();
    this.syncViewState();
  }

  private syncViewState(): void {
    this.overlayButtonIcon = this.isPlaying ? 'pause' : 'play';
    this.shouldCenterOverlayButton = this.isVerticalLayout && !this.isFullscreen;
    this.shouldShowControlsToggle = this.showControlsSig() && this.isCompactLayout;

    if (this.isCompactLayout && this.areControlsExpanded) {
      this.shouldShowOverlayButton = false;
      return;
    }

    if (!this.hasStartedPlayback) {
      this.shouldShowOverlayButton = true;
      return;
    }

    if (this.isFullscreen && (this.isVerticalLayout || this.isNarrowVideo || this._wasCompactLayoutBeforeFullscreen)) {
      this.shouldShowOverlayButton = true;
      return;
    }

    this.shouldShowOverlayButton = this.isPlaying ? this.isPointerOverVideo : true;
  }

  private buildShareUrl(actionKey: VimeoShareActionKey): string | null {
    if (!this.shareLink) {
      return null;
    }

    const videoId = this.resolveVideoId({ link: this.shareLink });
    const videoTitle = encodeURIComponent(this._videoTitle ?? '');
    const videoLink = encodeURIComponent(this.shareLink);

    if (actionKey === 'mail') {
      const mailSubject = encodeURIComponent(`Check out “${this._videoTitle ?? ''}” from Alliance Group on Vimeo`);
      const mailBody = encodeURIComponent(
        `Check out “${this._videoTitle ?? ''}” from Alliance Group on Vimeo.\n\nThe video is available for your viewing pleasure at ${this.shareLink}.`,
      );

      return `mailto:?subject=${mailSubject}&body=${mailBody}`;
    }

    if (actionKey === 'linkedin') {
      return `https://www.linkedin.com/shareArticle?mini=true&url=${videoLink}&title=${videoTitle}`;
    }

    if (actionKey === 'tumblr') {
      return `https://www.tumblr.com/widgets/share/tool?posttype=link&canonicalUrl=${videoLink}&title=${videoTitle}`;
    }

    if (!videoId) {
      return null;
    }

    if (actionKey === 'facebook') {
      return `https://player.vimeo.com/video/${videoId}/share/facebook`;
    }

    if (actionKey === 'twitter') {
      return `https://player.vimeo.com/video/${videoId}/share/twitter`;
    }

    return null;
  }
}
